import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, ArrowRightIcon } from 'lucide-react';
export function HeroSection() {
  return <section className="bg-brand-dark text-white py-16 md:py-24 px-4 overflow-hidden" data-id="element-99">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-100">
        {/* Left Column: Copy & CTAs */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="max-w-2xl" data-id="element-101">
          <span className="uppercase tracking-widest text-sm font-bold text-brand-accent mb-4 block" data-id="element-102">
            Lancer Service · St. Paul, MN
          </span>
          <h1 className="font-heading uppercase tracking-wide text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6" data-id="element-103">
            Before approving an expensive dealership repair, get a second
            opinion.
          </h1>
          <div className="text-lg md:text-xl text-gray-300 mb-8 space-y-4" data-id="element-104">
            <p data-id="element-105">
              Large repair estimates often happen when expensive parts are
              replaced before the real problem is found.
            </p>
            <p data-id="element-106">
              Our ASE-certified technicians inspect the full system first and
              show you exactly what we find before recommending any repair.
            </p>
            <p data-id="element-107">
              Dealerships are not the only shops equipped to diagnose modern
              vehicles. We use advanced diagnostic tools, manufacturer service
              data, and OEM-quality parts comparable to those used by
              dealerships.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-8" data-id="element-108">
            <a href="tel:6512240267" className="inline-flex justify-center items-center px-8 py-4 bg-brand-accent hover:bg-brand-accentHover text-white font-bold rounded-md transition-colors shadow-lg shadow-brand-accent/20" data-id="element-109">
              Schedule Free Inspection
            </a>
            <a href="#examples" className="inline-flex justify-center items-center px-8 py-4 bg-transparent border-2 border-gray-600 hover:border-gray-400 text-white font-bold rounded-md transition-colors" data-id="element-110">
              See Real Repair Examples
            </a>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 text-sm text-gray-400 font-medium" data-id="element-111">
            <div className="flex items-center gap-2" data-id="element-112">
              <CheckCircleIcon className="w-4 h-4 text-brand-accent" data-id="element-113" />
              <span data-id="element-114">No-pressure estimates</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-115">
              <CheckCircleIcon className="w-4 h-4 text-brand-accent" data-id="element-116" />
              <span data-id="element-117">Photos & video of what we find</span>
            </div>
          </div>
        </motion.div>

        {/* Right Column: Proof Cards */}
        <div className="relative lg:pl-12" data-id="element-118">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="mb-4 text-center lg:text-left" data-id="element-119">
            <span className="inline-block bg-gray-800 text-gray-300 text-xs font-bold uppercase tracking-wider py-1 px-3 rounded-full border border-gray-700" data-id="element-120">
              Second opinions that saved our customers money
            </span>
          </motion.div>

          <div className="space-y-4" data-id="element-121">
            {/* Card 1 */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="bg-gray-800/80 border border-gray-700 p-6 rounded-xl shadow-2xl backdrop-blur-sm" data-id="element-122">
              <h3 className="font-heading uppercase tracking-wide font-bold text-xl mb-4 border-b border-gray-700 pb-3" data-id="element-123">
                2018 Ford Escape
              </h3>
              <div className="space-y-3 text-sm md:text-base" data-id="element-124">
                <div className="flex justify-between items-start gap-4" data-id="element-125">
                  <span className="text-gray-400" data-id="element-126">Dealership recommended:</span>
                  <span className="text-right line-through text-gray-500" data-id="element-127">
                    Turbocharger replacement
                  </span>
                </div>
                <div className="flex justify-between items-start gap-4" data-id="element-128">
                  <span className="text-gray-400" data-id="element-129">Lancer repaired:</span>
                  <span className="text-right font-medium text-white" data-id="element-130">
                    Cracked charge pipe
                  </span>
                </div>
                <div className="flex justify-between items-center gap-4 pt-3 mt-3 border-t border-gray-700/50" data-id="element-131">
                  <span className="font-semibold" data-id="element-132">Customer savings</span>
                  <span className="text-2xl font-bold text-brand-success" data-id="element-133">
                    $3,300+
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Card 2 */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} className="bg-gray-800/80 border border-gray-700 p-6 rounded-xl shadow-2xl backdrop-blur-sm" data-id="element-134">
              <h3 className="font-heading uppercase tracking-wide font-bold text-xl mb-4 border-b border-gray-700 pb-3" data-id="element-135">
                Lincoln Sedan
              </h3>
              <div className="space-y-3 text-sm md:text-base" data-id="element-136">
                <div className="flex justify-between items-start gap-4" data-id="element-137">
                  <span className="text-gray-400" data-id="element-138">Dealership recommended:</span>
                  <span className="text-right line-through text-gray-500" data-id="element-139">
                    Full wiring harness
                  </span>
                </div>
                <div className="flex justify-between items-start gap-4" data-id="element-140">
                  <span className="text-gray-400" data-id="element-141">Lancer repaired:</span>
                  <span className="text-right font-medium text-white" data-id="element-142">
                    Targeted wiring repair
                  </span>
                </div>
                <div className="flex justify-between items-center gap-4 pt-3 mt-3 border-t border-gray-700/50" data-id="element-143">
                  <span className="font-semibold" data-id="element-144">Customer savings</span>
                  <span className="text-2xl font-bold text-brand-success" data-id="element-145">
                    $2,400+
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}